import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/src/components/layouts/paginated-layout.js";
import { Subpage, Callout } from "course-components";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Subpage slug="language-in-ixd" mdxType="Subpage">
      <h3>{`Language in IxD`}</h3>
      <p>{`In this module we return to IxD1 to look at how the words we use in our designs affect interactions.`}</p>
      <Callout lead={true} color="secondary" mdxType="Callout">
        <p>{`Read IxD1, Chapter 2: Fundamentals of Language in IxD`}</p>
      </Callout>
      <Callout lead={true} color="secondary" mdxType="Callout">
        <p>{`Read IxD1, Chapter 3: Create Amazing User Conversations with Your Copy.`}</p>
      </Callout>
      <h4>{`Study Questions`}</h4>
      <ol>
        <li parentName="ol">{`What are the 4 main duties of copy?`}</li>
        <li parentName="ol">{`What is the value of understanding a user’s context when writing copy?`}</li>
        <li parentName="ol">{`List the items in Des Traynor’s checklist for understanding audience and context.`}</li>
        <li parentName="ol">{`What are the book’s 4 tips for good copywriting?`}</li>
        <li parentName="ol">{`Describe the role personality should play in good copywriting.`}</li>
        <li parentName="ol">{`Describe the role emotional connection should play in good copywriting.`}</li>
        <li parentName="ol">{`What are two common layout patterns to consider for placing copy?`}</li>
        <li parentName="ol">{`What are two keys for using typography well?`}</li>
        <li parentName="ol">{`What role does microcopy play in good copywriting?`}</li>
      </ol>
    </Subpage>
    <Subpage slug="strings-and-booleans" mdxType="Subpage">
      <h3>{`Strings and Booleans and All Their Wordy, Truthy Buddies`}</h3>
      <p>{`Now let's switch gears and look a little more about programming, specifically working with strings in JavaScript.`}</p>
      <Callout color="secondary" mdxType="Callout">
        <p>{`Read the following from the Duckett text:`}</p>
        <ul>
          <li parentName="ul">{`Ch.2, pp.64--68, 78--84 to learn basics of how to work with booleans and strings.`}</li>
          <li parentName="ul">{`Ch.4, pp.145--159 to learn about comparison operators and logical operators and a little more about booleans.`}</li>
        </ul>
      </Callout>
      <h4>{`Study Questions`}</h4>
      <ul>
        <li parentName="ul">{`What is the concept of escaping in strings?`}</li>
        <li parentName="ul"><strong parentName="li">{`SKILL:`}</strong>{` Create string values.`}</li>
        <li parentName="ul"><strong parentName="li">{`SKILL:`}</strong>{` Create boolean values.`}</li>
        <li parentName="ul"><strong parentName="li">{`SKILL:`}</strong>{` Use the string/concatenation operator to combine strings.
What are comparison operators and how are they used?`}</li>
        <li parentName="ul"><strong parentName="li">{`SKILL:`}</strong>{` Use the comparison operators to reduce an expression to a boolean value.
What are logical operators and how are they used?`}</li>
        <li parentName="ul"><strong parentName="li">{`SKILL:`}</strong>{` Use the logical operators to reduce an expression to a boolean value.`}</li>
      </ul>
    </Subpage>
    <Subpage slug="becoming-efficient-with-functions" mdxType="Subpage">
      <h3>{`Becoming Efficient with Functions`}</h3>
      <p>{`A major topic we'll continue to expand on in the future is that of functions in JavaScript.`}</p>
      <Callout lead={true} color="secondary" mdxType="Callout">
        <p>{`Read Duckett, Ch.3, pp.85--99 to learn more about functions.`}</p>
      </Callout>
      <h4>{`Study Questions`}</h4>
      <ul>
        <li parentName="ul">{`What are functions?`}</li>
        <li parentName="ul">{`What are parameters?`}</li>
        <li parentName="ul">{`What is a return value?`}</li>
        <li parentName="ul"><strong parentName="li">{`SKILL:`}</strong>{` Declare both named and unnamed functions that include one or more parameters and return a value or variable.`}</li>
        <li parentName="ul"><strong parentName="li">{`SKILL:`}</strong>{` Call an existing function, with or without parameters.`}</li>
        <li parentName="ul">{`What is variable scope?`}</li>
      </ul>
    </Subpage>
    <Subpage slug="presentation-and-demo" mdxType="Subpage">
      <h3>{`Presentation and Demo`}</h3>
      <p>{`Watch this presentation and play along in the demo provided in CULearn. Use `}<a parentName="p" {...{
          "href": "/docs/vcd-3650-lesson-3.pdf"
        }}>{`this handout`}</a>{` to take notes.`}</p>
      <iframe width="560" height="315" src="https://www.youtube.com/embed/Ah3nN_FnPg8" frameborder="0" allowfullscreen></iframe>
    </Subpage>
    <Subpage slug="commit-to-memory" mdxType="Subpage">
      <h3>{`Commit to Memory`}</h3>
      <h4>{`Keywords`}</h4>
      <ul>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`true`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`false`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`function`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`return`}</code></li>
      </ul>
      <h4>{`Operators`}</h4>
      <p>{`String operators (concatenation)`}</p>
      <ul>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`+`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`+=`}</code></li>
      </ul>
      <p>{`Comparison operators`}</p>
      <ul>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`>`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`>=`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`<`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`<=`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`==`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`!=`}</code></li>
      </ul>
      <p>{`Logical operators`}</p>
      <ul>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`||`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`&&`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`xor`}</code></li>
        <li parentName="ul"><code parentName="li" {...{
            "className": "language-text"
          }}>{`!`}</code></li>
      </ul>
      <p>{`Snippets`}</p>
      <ul>
        <li parentName="ul">
          <p parentName="li">{`Declaring a function:`}</p>
          <div parentName="li" {...{
            "className": "gatsby-highlight",
            "data-language": "js"
          }}><pre parentName="div" {...{
              "style": {
                "counterReset": "linenumber NaN"
              },
              "className": "language-js line-numbers"
            }}><code parentName="pre" {...{
                "className": "language-js"
              }}><span parentName="code" {...{
                  "className": "token keyword"
                }}>{`function`}</span>{` `}<span parentName="code" {...{
                  "className": "token function"
                }}>{`__1__`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`(`}</span><span parentName="code" {...{
                  "className": "token parameter"
                }}>{`__2__`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`)`}</span>{` `}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`{`}</span>{`
    __3__
`}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`}`}</span></code><span parentName="pre" {...{
                "aria-hidden": "true",
                "className": "line-numbers-rows",
                "style": {
                  "whiteSpace": "normal",
                  "width": "auto",
                  "left": "0"
                }
              }}><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span></span></pre></div>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Calling a function:`}</p>
          <div parentName="li" {...{
            "className": "gatsby-highlight",
            "data-language": "js"
          }}><pre parentName="div" {...{
              "style": {
                "counterReset": "linenumber NaN"
              },
              "className": "language-js line-numbers"
            }}><code parentName="pre" {...{
                "className": "language-js"
              }}><span parentName="code" {...{
                  "className": "token function"
                }}>{`__1__`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`(`}</span>{`__2__`}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`)`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`;`}</span></code><span parentName="pre" {...{
                "aria-hidden": "true",
                "className": "line-numbers-rows",
                "style": {
                  "whiteSpace": "normal",
                  "width": "auto",
                  "left": "0"
                }
              }}><span parentName="span"></span></span></pre></div>
        </li>
        <li parentName="ul">
          <p parentName="li">{`The DRE all-in-one:`}</p>
          <div parentName="li" {...{
            "className": "gatsby-highlight",
            "data-language": "js"
          }}><pre parentName="div" {...{
              "style": {
                "counterReset": "linenumber NaN"
              },
              "className": "language-js line-numbers"
            }}><code parentName="pre" {...{
                "className": "language-js"
              }}><span parentName="code" {...{
                  "className": "token function"
                }}>{`$`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`(`}</span>{`document`}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`)`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`.`}</span><span parentName="code" {...{
                  "className": "token function"
                }}>{`ready`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`(`}</span><span parentName="code" {...{
                  "className": "token keyword"
                }}>{`function`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`(`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`)`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`{`}</span>{`
    `}<span parentName="code" {...{
                  "className": "token comment"
                }}>{`// Stuff to do when document is loaded`}</span>{`
`}<span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`}`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`)`}</span><span parentName="code" {...{
                  "className": "token punctuation"
                }}>{`;`}</span></code><span parentName="pre" {...{
                "aria-hidden": "true",
                "className": "line-numbers-rows",
                "style": {
                  "whiteSpace": "normal",
                  "width": "auto",
                  "left": "0"
                }
              }}><span parentName="span"></span><span parentName="span"></span><span parentName="span"></span></span></pre></div>
        </li>
      </ul>
    </Subpage>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      